import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Eye, Search, Undo2 } from "lucide-react";
import { api, API, formatApiError } from "../api";
import { MONEY } from "../constants/testIds";
import { formatCurrency } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { useSortPersistence } from "../hooks/useSortPersistence";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { SortControl } from "../components/common/SortControl";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "../components/ui/sonner";
import { useCelebration } from "../contexts/CelebrationContext";

const TABS = [
  { key: "overview", label: "Overview" }, { key: "ledger", label: "Ledger" },
  { key: "refunds", label: "Refunds" }, { key: "invoices", label: "Invoices" },
];
const LEDGER_TYPES = ["dues_accrual", "payment_cash", "payment_digital", "refund", "advance", "take_rate", "wallet_credit", "wallet_debit"];
const LEDGER_SORT_FIELDS = [{ value: "at", label: "Newest first", type: "date" }, { value: "amount", label: "Amount", type: "numeric" }];

function daysAgoIso(n) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

// UX-GAP-01 §1.4: Owner Cockpit "Money" — was a StubPage.
export default function Money() {
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("overview");

  return (
    <div className="space-y-5 max-w-5xl" data-testid={MONEY.page}>
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Money</h1>
        <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Revenue, the ledger, refunds, and invoices — all reconciled to the same numbers.</p>
      </motion.div>

      <div className="flex gap-1.5 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={MONEY.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "ledger" && <LedgerTab />}
      {tab === "refunds" && <RefundsTab />}
      {tab === "invoices" && <InvoicesTab />}
    </div>
  );
}

function OverviewTab() {
  const summaryQ = useQuery({ queryKey: ["money-revenue-summary"], queryFn: async () => (await api.get("/revenue/summary")).data });
  const integrityMutation = useMutation({
    mutationFn: async () => (await api.get("/reconciliation/integrity-check")).data,
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (summaryQ.isError) return <ErrorState error={summaryQ.error} onRetry={() => summaryQ.refetch()} />;
  if (summaryQ.isLoading) return <GymLoader label="Loading revenue…" />;
  const s = summaryQ.data;

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3">
        <Tile testId={MONEY.revenueTile} label="Gross revenue" value={formatCurrency(s.revenue)} />
        <Tile testId={MONEY.netRevenueTile} label="Net revenue" value={formatCurrency(s.net_revenue)} />
        <Tile testId={MONEY.refundsTile} label="Refunds issued" value={formatCurrency(s.refunds_total)} negative />
        <Tile testId={MONEY.duesTile} label="Outstanding dues" value={formatCurrency(s.outstanding_dues)} negative />
      </div>
      <div className="solid-surface rounded-[14px] p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>Ledger integrity check</p>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Re-derives every membership's dues/advance straight from the ledger and flags any drift.</p>
        </div>
        <Button data-testid={MONEY.integrityCheckButton} onClick={() => integrityMutation.mutate()} disabled={integrityMutation.isPending} variant="outline">
          {integrityMutation.isPending ? "Checking…" : "Run check"}
        </Button>
      </div>
      {integrityMutation.data && (
        <div data-testid={MONEY.integrityResult} className="flex items-center gap-2 text-sm rounded-[10px] px-4 py-3" style={{ background: integrityMutation.data.drift_found ? "rgba(214, 32, 43,0.08)" : "rgba(16,163,74,0.08)", color: integrityMutation.data.drift_found ? "var(--scarlet-neg)" : "#0f8a3f" }}>
          <CheckCircle2 size={15} />
          {integrityMutation.data.drift_found ? `${integrityMutation.data.drift_found} membership(s) drifted from the ledger — an alert has been raised.` : `Checked ${integrityMutation.data.checked} memberships, zero drift. Everything reconciles.`}
        </div>
      )}
    </div>
  );
}

function Tile({ testId, label, value, negative }) {
  return (
    <div data-testid={testId} className="solid-surface rounded-[14px] px-4 py-4">
      <p className="eyebrow">{label}</p>
      <p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: negative ? "var(--scarlet-neg)" : "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}

function LedgerTab() {
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(daysAgoIso(0));
  const [type, setType] = useState("all");
  // DEFECT-D-29/D-30: this table previously always showed oldest-first with no way to change it.
  const { sortBy, sortDir, setSort } = useSortPersistence("money-ledger", "at", "desc");

  const ledgerQ = useQuery({
    queryKey: ["money-ledger", dateFrom, dateTo, type, sortBy, sortDir],
    queryFn: async () => (await api.get("/ledger", { params: { date_from: `${dateFrom}T00:00:00Z`, date_to: `${dateTo}T23:59:59Z`, type: type === "all" ? undefined : type, sort_by: sortBy, sort_dir: sortDir } })).data.entries,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input data-testid={MONEY.ledgerDateFrom} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
        <span style={{ color: "var(--scarlet-text-secondary)" }}>to</span>
        <Input data-testid={MONEY.ledgerDateTo} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        <Select onValueChange={setType} value={type}>
          <SelectTrigger data-testid={MONEY.ledgerTypeSelect} className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {LEDGER_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <SortControl fields={LEDGER_SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={MONEY.ledgerSortControl} />
      </div>

      {ledgerQ.isError && <ErrorState error={ledgerQ.error} onRetry={() => ledgerQ.refetch()} />}
      {ledgerQ.isLoading && <GymLoader label="Loading ledger…" />}
      {ledgerQ.data && ledgerQ.data.length === 0 && (
        <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={MONEY.ledgerEmptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No ledger entries in this window.</div>
      )}
      {ledgerQ.data && ledgerQ.data.length > 0 && (
        <div className="solid-surface rounded-[14px] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid={MONEY.ledgerTable}>
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <th className="eyebrow px-5 py-3">When</th>
                <th className="eyebrow px-5 py-3">Type</th>
                <th className="eyebrow px-5 py-3">Member ref</th>
                <th className="eyebrow px-5 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {ledgerQ.data.slice(0, 300).map((e) => (
                <tr key={e._id} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                  <td className="px-5 py-2.5 text-xs whitespace-nowrap" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(e.at).toLocaleString()}</td>
                  <td className="px-5 py-2.5" style={{ color: "var(--scarlet-text)" }}>{e.type.replace(/_/g, " ")}</td>
                  <td className="px-5 py-2.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{e.member_ref ? `…${e.member_ref.slice(-8)}` : "—"}</td>
                  <td className="px-5 py-2.5 tabular-nums-force font-medium" style={{ color: e.direction === "credit" ? "#0f8a3f" : "var(--scarlet-text)" }}>{e.direction === "credit" ? "+" : "−"}{formatCurrency(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Same search -> pick a payment -> type-to-confirm refund flow the Manager's "Refund approval"
// screen already uses (`payment.refund` sits on super_admin too) — just Owner-Cockpit-styled.
function RefundsTab() {
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [member, setMember] = useState(null);
  const [target, setTarget] = useState(null);

  const searchQ = useQuery({
    queryKey: ["money-refund-search", submitted],
    queryFn: async () => {
      const isPhone = /^\+?\d[\d\s-]{5,}$/.test(submitted);
      const params = isPhone ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted };
      return (await api.get("/members", { params: { ...params, limit: 10 } })).data.members;
    },
    enabled: !!submitted,
  });

  const ledgerQ = useQuery({
    queryKey: ["money-refund-ledger", member?.id],
    queryFn: async () => (await api.get("/ledger", { params: { member_ref: member.id } })).data.entries,
    enabled: !!member,
  });

  const payments = (ledgerQ.data || []).filter((e) => ["payment_cash", "payment_digital"].includes(e.type) && e.direction === "credit");
  const refundedIds = new Set((ledgerQ.data || []).filter((e) => e.type === "refund").map((e) => e.related_ref));

  const refundMutation = useMutation({
    mutationFn: ({ paymentRef, amount, reason }) => api.post("/refunds", { payment_ref: paymentRef, amount, reason, to: "rail" }, { headers: { "Idempotency-Key": crypto.randomUUID() } }),
    onSuccess: () => { celebrate("refund_processed"); setTarget(null); queryClient.invalidateQueries({ queryKey: ["money-refund-ledger"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <form onSubmit={(e) => { e.preventDefault(); setMember(null); setSubmitted(query.trim()); }} className="flex gap-2">
        <Input data-testid={MONEY.refundSearchInput} placeholder="Member name or phone…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button type="submit" data-testid={MONEY.refundSearchButton} disabled={!query.trim()} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}><Search size={15} /> Find</Button>
      </form>

      {!member && searchQ.data?.map((m) => (
        <button key={m.id} data-testid={MONEY.refundMemberRow(m.id)} onClick={() => setMember(m)} className="w-full flex items-center justify-between rounded-[12px] px-4 py-3 text-left solid-surface scarlet-focusable">
          <div>
            <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>{m.profile.name}</p>
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{m.profile.phone}</p>
          </div>
        </button>
      ))}

      {member && (
        <div className="solid-surface rounded-[14px] overflow-hidden">
          <p className="px-4 py-3 text-sm font-semibold" style={{ color: "var(--scarlet-text)", borderBottom: "1px solid var(--scarlet-hairline)" }}>{member.profile.name}'s payments</p>
          {payments.length === 0 && <p className="p-4 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No refundable payments found.</p>}
          {payments.map((p) => {
            const already = refundedIds.has(p.related_ref);
            return (
              <div key={p._id} data-testid={MONEY.refundEntryRow(p._id)} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(p.amount)} · {p.type === "payment_cash" ? "Cash" : "Digital"}</p>
                  <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(p.at).toLocaleString()}</p>
                </div>
                {already ? <StatusPill status="overdue" label="Refunded" /> : (
                  <Button size="sm" variant="outline" data-testid={MONEY.refundButton(p._id)} onClick={() => setTarget(p)} className="gap-1"><Undo2 size={13} /> Refund</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {target && (
        <AlertDialog open onOpenChange={(v) => !v && setTarget(null)}>
          <RefundConfirmContent target={target} memberName={member?.profile?.name} busy={refundMutation.isPending} onConfirm={(payload) => refundMutation.mutate(payload)} />
        </AlertDialog>
      )}
    </div>
  );
}

function RefundConfirmContent({ target, memberName, busy, onConfirm }) {
  const [reason, setReason] = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const maxAmount = target.amount / 100;
  const amount = Math.round(Number(amountRupees || maxAmount) * 100);
  const valid = reason.trim() && confirmText === memberName && amount > 0 && amount <= target.amount;

  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Refund {formatCurrency(target.amount)} to {memberName}?</AlertDialogTitle>
        <AlertDialogDescription>Original payment on {new Date(target.at).toLocaleString()}. Type <strong>{memberName}</strong> to confirm.</AlertDialogDescription>
      </AlertDialogHeader>
      <div className="space-y-2">
        <Input data-testid={MONEY.refundAmountInput} type="number" min={1} max={maxAmount} placeholder={`Amount (₹, up to ${maxAmount})`} value={amountRupees} onChange={(e) => setAmountRupees(e.target.value)} />
        <Input data-testid={MONEY.refundReasonInput} placeholder="Reason for refund (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Input data-testid={MONEY.refundConfirmInput} placeholder={memberName} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={MONEY.refundConfirmSubmit} disabled={!valid || busy} onClick={() => onConfirm({ paymentRef: target.related_ref, amount, reason })} className="text-white" style={{ background: "var(--scarlet-neg)" }}>
          Confirm refund
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}

function InvoicesTab() {
  const invoicesQ = useQuery({ queryKey: ["money-invoices"], queryFn: async () => (await api.get("/invoices")).data.invoices });

  if (invoicesQ.isError) return <ErrorState error={invoicesQ.error} onRetry={() => invoicesQ.refetch()} />;
  if (invoicesQ.isLoading) return <GymLoader label="Loading invoices…" />;
  if (invoicesQ.data.length === 0) return <div className="solid-surface rounded-[14px] p-10 text-center text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No invoices yet.</div>;

  return (
    <div className="solid-surface rounded-[14px] overflow-hidden">
      <table className="w-full text-sm" data-testid={MONEY.invoiceTable}>
        <thead>
          <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
            <th className="eyebrow px-5 py-3">Invoice #</th>
            <th className="eyebrow px-5 py-3">Issued</th>
            <th className="eyebrow px-5 py-3">Total</th>
            <th className="eyebrow px-5 py-3">Status</th>
            <th className="eyebrow px-5 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {invoicesQ.data.slice(0, 100).map((inv) => (
            <tr key={inv.id} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
              <td className="px-5 py-2.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{inv.invoice_number}</td>
              <td className="px-5 py-2.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(inv.issued_at).toLocaleDateString()}</td>
              <td className="px-5 py-2.5 tabular-nums-force font-medium" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(inv.total)}</td>
              <td className="px-5 py-2.5"><StatusPill status="active" label={inv.status} /></td>
              <td className="px-5 py-2.5 text-right">
                <div className="inline-flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`${MONEY.invoiceDownloadButton(inv.id)}-show`} onClick={() => window.open(`${API}/invoices/${inv.id}/pdf?inline=true`, "_blank")} className="gap-1"><Eye size={13} /> Show</Button>
                  <Button size="sm" variant="outline" data-testid={MONEY.invoiceDownloadButton(inv.id)} onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")}>Download</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
